/**
 * One grid cell: an hls.js instance wired to the `peepshow://` loader.
 *
 * Everything decryption-related is per-cell and torn down with the cell, so
 * four of these can run side by side without sharing key material or leaking
 * an instance when the layout changes.
 */

import Hls from 'hls.js';
import { useEffect, useRef, useState } from 'react';

import { cachedUuids } from '../blobCache';
import { createPeepshowLoader, masterUrl } from '../peepshowLoader';
import { unwrapContentKey, zero } from '../crypto';
import { isSlideshow, type Item, type VideoPayload } from '../types';
import { useVault } from '../vault';
import { SlideshowCell } from './SlideshowCell';

interface Props {
  video: Item<VideoPayload> | null;
  /** 0..1. Applied to the element and kept in step with the native slider. */
  volume: number;
  onVolume(volume: number): void;
  onEnded(): void;
  onClear(): void;
  onNext(): void;
  onPrev(): void;
  /** How many videos this slot is cycling through, and where it is. */
  queueLength: number;
  queuePosition: number;
  label: string;
  /** This slot receives whatever is clicked in the library. */
  selected: boolean;
  onSelect(): void;
  /** The cell the library and the keyboard are aimed at. */
  active?: boolean;
  /** Seconds per slide for a slideshow, if the wall has an opinion. */
  slideSpeed?: number;
  onSlideSpeed?(seconds: number): void;
}

/**
 * Can this browser set an element's volume at all?
 *
 * iOS refuses: the level is the hardware buttons' business there, and
 * assignments to `volume` are silently ignored. A slider that does nothing is
 * worse than a mute button that works, so ask once and offer the right one.
 */
const CAN_SET_VOLUME = (() => {
  if (typeof document === 'undefined') return true;
  const probe = document.createElement('audio');
  probe.volume = 0.5;
  return probe.volume === 0.5;
})();

export function PlayerCell({
  video,
  volume,
  onVolume,
  onEnded,
  onClear,
  onNext,
  onPrev,
  queueLength,
  queuePosition,
  label,
  selected,
  active,
  slideSpeed,
  onSlideSpeed,
  onSelect,
}: Props) {
  const { keys, rungSegments, levelOrder } = useVault();
  // Decided before anything else: the hls.js effect must not run for a
  // slideshow, which has no manifest for it to load.
  const slideshow = Boolean(video && isSlideshow(video.payload));
  /**
   * Held in a ref, never in the effect's dependencies.
   *
   * These are rebuilt whenever the cached-blob set changes — which is once per
   * blob during a preload. As dependencies they tore down and recreated hls.js
   * on every segment, restarting playback from zero.
   */
  const rungSegmentsRef = useRef(rungSegments);
  rungSegmentsRef.current = rungSegments;
  const levelOrderRef = useRef(levelOrder);
  levelOrderRef.current = levelOrder;
  const videoRef = useRef<HTMLVideoElement>(null);
  const [error, setError] = useState<string | null>(null);
  const [ready, setReady] = useState(false);
  /** The rung hls.js is currently playing, as it switches. */
  const [level, setLevel] = useState<{
    index: number;
    width: number;
    height: number;
    bitrate: number;
  } | null>(null);
  /** Live view of the cache, read inside callbacks without re-running them. */
  const { cached } = useVault();
  const cachedNow = useRef(cached);
  cachedNow.current = cached;
  /** Whether the fragment now playing came off the disk. */
  const [fromCache, setFromCache] = useState(false);
  /**
   * Set once frames are actually on screen, which is what the fade waits for.
   *
   * `ready` fires when the manifest parses — well before there is a picture,
   * so fading in on it just shows black slightly more slowly.
   */
  const [visible, setVisible] = useState(false);


  /**
   * Volume lives in a ref for the effect and in props for the element.
   *
   * Every cell can make sound at once — that is the point of a wall — so there
   * is no single designated audio cell. Each slot remembers its own level, set
   * with the player's own volume control.
   */
  /**
   * Fade the outgoing video to black *before* it ends.
   *
   * Reacting to `ended` is already too late: the video has stopped on its last
   * frame, so the stop is seen at full brightness and only then does the pane
   * dim. Starting the fade a moment early means the picture is gone by the
   * time playback finishes, and the switch happens in darkness.
   */
  const FADE_LEAD_SECONDS = 0.7;
  const onEndedRef = useRef(onEnded);
  onEndedRef.current = onEnded;
  const queueLengthRef = useRef(queueLength);
  queueLengthRef.current = queueLength;

  const volumeRef = useRef(volume);
  volumeRef.current = volume;
  const onVolumeRef = useRef(onVolume);
  onVolumeRef.current = onVolume;

  useEffect(() => {
    if (slideshow) return;
    const element = videoRef.current;
    if (!element || !video || !keys) return;

    let hls: Hls | null = null;
    let contentKey: Uint8Array | null = null;
    let cancelled = false;
    setError(null);
    setReady(false);
    setLevel(null);
    setFromCache(false);
    setVisible(false);


    /*
     * No fullscreen.
     *
     * `controlsList` hides the button, but only Chromium honours it, and it
     * does nothing about the F key or the double-click gesture. So the
     * attribute is a hint and the event handler is the actual rule: whatever
     * route is taken into fullscreen, we come straight back out.
     */
    element.setAttribute('controlsList', 'nofullscreen');
    const leaveFullscreen = () => {
      if (document.fullscreenElement === element) void document.exitFullscreen?.();
    };
    document.addEventListener('fullscreenchange', leaveFullscreen);
    // iOS Safari never enters the Fullscreen API; the video element goes
    // fullscreen on its own and only answers to its own methods.
    const leaveNativeFullscreen = () => {
      (element as HTMLVideoElement & { webkitExitFullscreen?: () => void })
        .webkitExitFullscreen?.();
    };
    element.addEventListener('webkitbeginfullscreen', leaveNativeFullscreen);

    // Apply the stored level, and follow the native slider back into state.
    // Debounced: dragging fires volumechange continuously and each save is an
    // encrypted PATCH.
    element.volume = Math.min(1, Math.max(0, volumeRef.current));
    element.muted = volumeRef.current <= 0;
    let saveTimer: ReturnType<typeof setTimeout> | undefined;
    const onVolumeChange = () => {
      const level = element.muted ? 0 : element.volume;
      if (Math.abs(level - volumeRef.current) < 0.01) return;
      clearTimeout(saveTimer);
      saveTimer = setTimeout(() => onVolumeRef.current(level), 800);
    };
    element.addEventListener('volumechange', onVolumeChange);

    const onPlaying = () => setVisible(true);
    element.addEventListener('playing', onPlaying);

    const onTimeUpdate = () => {
      const { duration, currentTime } = element;
      if (!Number.isFinite(duration) || duration <= 0) return;
      if (duration - currentTime <= FADE_LEAD_SECONDS) setVisible(false);
    };
    element.addEventListener('timeupdate', onTimeUpdate);

    const onFinished = () => {
      // A queue of one has nowhere to advance to, and stopping on a black
      // frame is not what a wall should do. Restart it.
      if (queueLengthRef.current <= 1) {
        element.currentTime = 0;
        void element.play().catch(() => undefined);
        return;
      }
      onEndedRef.current();
    };
    element.addEventListener('ended', onFinished);

    (async () => {
      // Both are present on every video; only a slideshow lacks them, and this
      // effect has already returned for those.
      const wrapped = video.payload.content_key_wrapped;
      const master = video.payload.master_blob;
      if (!wrapped || !master) {
        setError('this item has no video to play');
        return;
      }
      const { key } = await unwrapContentKey(keys.wrap, wrapped);
      if (cancelled) return;
      contentKey = key;

      // Resolve which rungs are on disk *before* the player exists.
      //
      // Doing this after MANIFEST_PARSED was too late: hls.js requests its
      // first fragment immediately, and with no bandwidth history it starts at
      // the bottom rung. Setting startLevel a few hundred milliseconds later
      // changed nothing, so a fully cached 1080p copy still opened at 240p.
      const [rungs, order] = await Promise.all([
        rungSegmentsRef.current(video),
        levelOrderRef.current(video),
      ]);
      if (cancelled) return;

      /*
       * Read the cache directly rather than trusting React state.
       *
       * `cached` is filled by an effect after the library loads, and a cell can
       * mount before that lands — leaving the set empty exactly when the
       * starting level is decided. The badge, computed later, then reported
       * 1080p cached while playback had already begun at 240p.
       *
       * One keys() pass, once per cell, against the real cache.
       */
      const onDisk = await cachedUuids();
      if (cancelled) return;
      const isCached = (uuid: string | undefined) =>
        Boolean(uuid) && (onDisk.has(uuid!) || cachedNow.current.has(uuid!));

      if (!Hls.isSupported()) {
        // Safari plays HLS natively, but native playback would have to fetch
        // the key over the network — precisely what this design forbids.
        setError('This browser cannot play encrypted HLS without MSE.');
        return;
      }

      const byManifest = new Map(rungs.map((r) => [r.manifestBlob, r] as const));
      const byManifestSegments = new Map(rungs.map((r) => [r.manifestBlob, r.segments] as const));
      // Filled in once hls.js reports its levels; the uuid lists are already
      // known, only the index mapping has to wait.
      const levelSegments = new Map<number, string[]>();

      /*
       * The level to pin to: the rung with the most cached segments, ties going
       * to the higher quality.
       *
       * Coverage rather than just the opening segment, because pinning lasts
       * for the whole video. A rung cached only at its start would strand the
       * rest on the network at that quality.
       */
      let startLevel = -1;
      let bestCoverage = 0;
      order.forEach((manifestBlob, index) => {
        const segments = byManifestSegments.get(manifestBlob) ?? [];
        const have = segments.reduce((n, uuid) => n + (isCached(uuid) ? 1 : 0), 0);
        if (have > 0 && have >= bestCoverage) {
          bestCoverage = have;
          startLevel = index;
        }
      });

      hls = new Hls({
        loader: createPeepshowLoader({
          metaKey: keys.meta,
          contentKey: key,
          onSource: (kind, _uuid, cached) => {
            if (kind === 'segment') setFromCache(cached);
          },
        }),
        // -1 leaves hls.js to choose, which is right when nothing is cached.
        startLevel,
        /*
         * No bandwidth probe.
         *
         * With startLevel -1, hls.js deliberately loads the *lowest* level's
         * first fragment to measure throughput, then switches up. On a video
         * wall that means every unforced start opens at 240p and climbs, and
         * the probe fragment is fetched over the network even when a better
         * copy of that same moment is already on disk.
         *
         * Without it, hls.js uses its bandwidth estimate to choose, which is
         * what we want when the cache cannot answer.
         */
        testBandwidth: false,
        enableWorker: true,
        lowLatencyMode: false,
        // Four concurrent streams: keep buffers modest so memory stays sane.
        maxBufferLength: 20,
        maxMaxBufferLength: 40,
        backBufferLength: 15,
      });

      // LEVEL_SWITCHED fires on the initial choice and on every ABR change, so
      // the readout follows the stream rather than what we asked for.
      hls.on(Hls.Events.LEVEL_SWITCHED, (_evt, data) => {
        const chosen = hls?.levels?.[data.level];
        if (chosen) {
          setLevel({
            index: data.level,
            width: chosen.width,
            height: chosen.height,
            bitrate: chosen.bitrate,
          });
        }
      });

      hls.on(Hls.Events.ERROR, (_evt, data) => {
        if (!data.fatal) return;
        setError(data.details ?? 'playback error');
      });

      hls.on(Hls.Events.MANIFEST_PARSED, (_evt, data) => {
        setReady(true);

        // Per-fragment quality from the cache.
        //
        // Rungs cut at the same instants, so segment i of 1080p and of 240p are
        // the same moment. If a better copy of the *next* fragment is already
        // on disk, play that one: it is free and it is better. ABR chooses only
        // where nothing is cached.
        // Prefer the master's ordering, which we derived ourselves; fall back
        // to reading each level's URL if the two disagree in length.
        if (order.length === data.levels.length) {
          order.forEach((manifestBlob, index) => {
            const segments = byManifestSegments.get(manifestBlob);
            if (segments) levelSegments.set(index, segments);
          });
        } else {
          data.levels.forEach((lvl, index) => {
            const blob = (lvl.url?.[0] ?? '').split('/').pop() ?? '';
            const rung = byManifest.get(blob);
            if (rung) levelSegments.set(index, rung.segments);
          });
        }

        /*
         * Pin the level outright.
         *
         * startLevel and nextLoadLevel are hints, and hls.js is entitled to
         * override them — it demotes levels it thinks are struggling and
         * re-evaluates on its own schedule. The same cached video opened at
         * 1080p once and 240p the next time with identical inputs, which is
         * what "advisory" looks like from outside.
         *
         * `currentLevel` is not advisory. Assigned once, at startup, it costs a
         * single flush at position zero and nothing after. The earlier black
         * screen came from assigning it on *every* fragment, which emptied the
         * buffer as fast as it filled; once is the supported use.
         *
         * Pinning gives up adaptation for cached videos, which is the trade
         * this whole design already makes: play what is on disk.
         */
        if (startLevel >= 0 && hls) hls.currentLevel = startLevel;
      });

      hls.on(Hls.Events.FRAG_LOADED, (_e, payload) => {
        if (!hls) return;
        // frag.sn is typed as number | 'initSegment'; only real fragments have
        // a position on the timeline.
        const sn = typeof payload.frag.sn === 'number' ? payload.frag.sn : -1;
        if (sn < 0) return;

        // No steering here any more: the level is pinned, so there is nothing
        // to steer. Only report whether this fragment came off the disk.
      });

      hls.attachMedia(element);
      hls.loadSource(masterUrl(video.uuid, master));

      // Autoplay with sound is blocked without a prior user gesture. Falling
      // back to muted beats a pane that silently refuses to start.
      void element.play().catch(() => {
        element.muted = true;
        void element.play().catch(() => undefined);
      });
    })().catch((err) => {
      if (!cancelled) setError(err instanceof Error ? err.message : String(err));
    });

    return () => {
      cancelled = true;
      clearTimeout(saveTimer);
      document.removeEventListener('fullscreenchange', leaveFullscreen);
      element.removeEventListener('webkitbeginfullscreen', leaveNativeFullscreen);
      element.removeEventListener('playing', onPlaying);
      element.removeEventListener('timeupdate', onTimeUpdate);
      element.removeEventListener('ended', onFinished);
      element.removeEventListener('volumechange', onVolumeChange);
      hls?.destroy();
      if (contentKey) zero(contentKey);
      // Deliberately not clearing src / calling load(): that blanks the element
      // to black instantly, which is the flash the fade is meant to replace.
      // Detaching MSE leaves the last frame up, so the outgoing video dims out
      // instead of vanishing. The element is unmounted when a slot is cleared,
      // so nothing stale survives.
    };
  }, [video, keys, slideshow]);

  // Our control is the only one on a phone, so its value has to reach the
  // element. The native UI's own changes still come back through
  // `volumechange`; this is the other direction.
  useEffect(() => {
    const element = videoRef.current;
    if (!element || slideshow) return;
    element.volume = Math.min(1, Math.max(0, volume));
    element.muted = volume <= 0;
  }, [volume, slideshow]);

  const cellClass = `cell${selected ? ' cell--selected' : ''}`;

  if (!video) {
    return (
      <div
        className={`${cellClass} cell--empty`}
        onClick={onSelect}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') onSelect();
        }}
      >
        <span className="cell__hint">{label}</span>
      </div>
    );
  }

  return (
    // Clicking anywhere in the cell aims the library at it. The video's own
    // controls keep working; selecting as a side effect of reaching for them is
    // the behaviour you would want anyway.
    <div className={cellClass} onClick={onSelect}>
      {slideshow ? (
        // Always visible: `visible` is driven by the video element's `playing`
        // event, which a slideshow never fires, so threading it through here
        // left the cell at opacity 0 with the images loading behind it. The
        // slideshow does its own fade, per slide.
        <SlideshowCell
          video={video}
          onEnded={onEnded}
          visible
          active={active}
          speed={slideSpeed}
          onSpeed={onSlideSpeed}
        />
      ) : (
        <video
          ref={videoRef}
          className="cell__video"
          // Fades out with the old video and back in once the new one is
          // actually rendering, turning a hard cut to black into a dissolve.
          style={{ opacity: visible ? 1 : 0 }}
          autoPlay
          playsInline
          controls
        />
      )}
      {level && !slideshow && (
        <span className="cell__level">
          {Math.min(level.width, level.height) || level.height}p ·{' '}
          {(level.bitrate / 1_000_000).toFixed(2)} Mbit/s{fromCache ? ' · cached' : ''}
        </span>
      )}

      <div className="cell__bar">
        {queueLength > 1 && (
          <>
            <button type="button" className="chip" onClick={onPrev} title="Previous video">
              ‹
            </button>
            <button type="button" className="chip" onClick={onNext} title="Next video">
              ›
            </button>
            <span className="cell__pos">
              {queuePosition + 1}/{queueLength}
            </span>
          </>
        )}
        {!slideshow &&
          (CAN_SET_VOLUME ? (
            <input
              className="cell__volume"
              type="range"
              min={0}
              max={1}
              step={0.05}
              value={volume}
              onChange={(event) => onVolume(Number(event.target.value))}
              // The cell selects itself on click; dragging the slider should
              // not also be a click on the wall behind it.
              onClick={(event) => event.stopPropagation()}
              title={`Volume ${Math.round(volume * 100)}%`}
              aria-label="Volume"
            />
          ) : (
            <button
              type="button"
              className="chip"
              onClick={(event) => {
                event.stopPropagation();
                onVolume(volume > 0 ? 0 : 1);
              }}
              title="This device sets the level with its own buttons"
            >
              {volume > 0 ? 'sound' : 'muted'}
            </button>
          ))}
        <span className="cell__title" title={video.payload.title}>
          {video.payload.title}
        </span>
        <button type="button" className="chip" onClick={onClear} title="Clear this cell">
          ×
        </button>
      </div>
      {!ready && !error && !slideshow && (
        <div className="cell__status">decrypting…</div>
      )}
      {error && <div className="cell__status cell__status--error">{error}</div>}
    </div>
  );
}
